import { useEffect, useRef, useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { CompanyRequestError } from '@/company/api';
import { formatCount } from '@/i18n/format';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import agentPermissionsCopy, { type AgentPermissionsCopy } from '@/i18n/ns/agent-permissions';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { WorkspaceIcon } from '../icons';
import { ConnectionMark } from '../your-connections/connection-mark';
import { agentAccessApi } from './api';
import { isLostResponse } from './format';
import { Glyph } from './glyphs';
import type { ApprovalAction, ApprovalGroup, FamilyApproval, KnowledgeClientGrant } from './types';

/** The most rows a group shows before "Show all": allowed ones always show. */
const SHOWN = 4;

/** The server's 409s, and anything else, in plain words. */
export function approvalErrorMessage(error: unknown, copy: AgentPermissionsCopy, app: string) {
  if (error instanceof CompanyRequestError) {
    if (error.code === 'scope_not_granted') return formatCopy(copy.errScopeNotGranted, { app });
    if (error.code === 'action_not_allowable') return copy.errNotAllowable;
    if (error.code === 'connection_read_only') return copy.errReadOnly;
  }
  return isLostResponse(error) ? copy.errNotConfirmed : copy.errNotSaved;
}

type Load<T> = { status: 'idle' | 'loading' | 'failed' } | { status: 'ok'; value: T };

const labelOf = (copy: AgentPermissionsCopy, action: ApprovalAction) =>
  (copy as Record<string, string>)[`action_${action.action}`] ?? action.label;

function noteOf(copy: AgentPermissionsCopy, action: ApprovalAction) {
  // A row that always asks says so, whatever else it could say.
  if (action.locked || action.floor) return copy.lockedNote;
  const own = (copy as Record<string, string>)[`note_${action.action}`];
  if (own) return own;
  return action.undoable ? copy.undoableNote : null;
}

const withAllowed = (group: ApprovalGroup, action: string, allowed: boolean): ApprovalGroup => {
  const actions = group.actions.map((entry) =>
    entry.action === action ? { ...entry, allowed } : entry,
  );
  return { ...group, actions, allowed: actions.filter((entry) => entry.allowed).length };
};

function Row({
  action,
  disabled,
  busy,
  onToggle,
}: {
  action: ApprovalAction;
  disabled: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  const copy = useNamespaceCopy(agentPermissionsCopy);
  const note = noteOf(copy, action);
  const locked = action.locked || action.floor;
  return (
    <li className="ws-allow-row" data-state={locked ? 'locked' : undefined}>
      <label className="ws-agent-check">
        <input
          type="checkbox"
          checked={action.allowed && !locked}
          disabled={locked || disabled}
          aria-disabled={busy || undefined}
          onChange={() => {
            if (!busy) onToggle();
          }}
        />
        <span>
          <strong>{labelOf(copy, action)}</strong>
          {note && (
            <small>
              {locked && <Glyph name="lock" size={12} />}
              {note}
            </small>
          )}
        </span>
      </label>
    </li>
  );
}

function Rows({
  group,
  name,
  readOnly,
  limit = Infinity,
  busy,
  onToggle,
}: {
  group: ApprovalGroup;
  name: string;
  readOnly: boolean;
  /** How many rows show before "Show all"; a native family shows every row. */
  limit?: number;
  busy: boolean;
  onToggle: (action: ApprovalAction) => void;
}) {
  const copy = useNamespaceCopy(agentPermissionsCopy);
  const [all, setAll] = useState(false);
  const shown = all
    ? group.actions
    : group.actions.filter((action, index) => index < limit || action.allowed);
  return (
    <>
      <ul className="ws-allow-list">
        {shown.map((action) => (
          <Row
            key={action.action}
            action={action}
            disabled={readOnly}
            busy={busy}
            onToggle={() => onToggle(action)}
          />
        ))}
      </ul>
      {group.actions.length > shown.length && (
        <button type="button" className="ws-allow-more" onClick={() => setAll(true)}>
          {formatCopy(copy.showAll, { count: String(group.actions.length), name })}
        </button>
      )}
      {all && group.actions.length > limit && (
        <button type="button" className="ws-allow-more" onClick={() => setAll(false)}>
          {copy.showFewer}
        </button>
      )}
    </>
  );
}

/**
 * "Allowed without asking": what this agent may do in a family without waiting
 * in the Inbox. Lists load when the well opens, and one connection's actions
 * when its group opens (Pipedream lists them live).
 */
export function AllowedWell({
  grantId,
  family,
  app,
  approval,
  onNotice,
  onChanged,
}: {
  grantId: string;
  family: string;
  app: string;
  approval: FamilyApproval;
  onNotice: (text: string, tone: 'ok' | 'error') => void;
  onChanged: (client: KnowledgeClientGrant) => void;
}) {
  const copy = useNamespaceCopy(agentPermissionsCopy);
  const locale = useChatInterfaceLocale();
  const [open, setOpen] = useState(approval.allowedCount > 0);
  const [index, setIndex] = useState<Load<ApprovalGroup[]>>({ status: 'idle' });
  const [details, setDetails] = useState<Record<string, Load<ApprovalGroup>>>({});
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadIndex = () => {
    setIndex({ status: 'loading' });
    agentAccessApi
      .approvalActions(grantId, family)
      .then((body) => alive.current && setIndex({ status: 'ok', value: body.groups }))
      .catch(() => alive.current && setIndex({ status: 'failed' }));
  };
  useEffect(() => {
    if (open && index.status === 'idle') loadIndex();
  }, [open, index.status]);

  const loadDetail = (id: string) => {
    setDetails((current) => ({ ...current, [id]: { status: 'loading' } }));
    agentAccessApi
      .approvalActions(grantId, family, id)
      .then((body) => {
        const group = body.groups.find((entry) => entry.connection?.id === id) ?? body.groups[0];
        if (!alive.current) return;
        setDetails((current) => ({
          ...current,
          [id]: group ? { status: 'ok', value: group } : { status: 'failed' },
        }));
      })
      .catch(() => {
        if (alive.current) setDetails((current) => ({ ...current, [id]: { status: 'failed' } }));
      });
  };

  const toggleGroup = (id: string) => {
    const opening = !openGroups.includes(id);
    setOpenGroups((current) =>
      opening ? [...current, id] : current.filter((entry) => entry !== id),
    );
    if (opening && (details[id]?.status ?? 'idle') === 'idle') loadDetail(id);
  };

  /** Ticks or unticks one action: the list changes at once and returns if the server refuses. */
  const change = async (group: ApprovalGroup, action: ApprovalAction) => {
    if (busy) return;
    const id = group.connection?.id ?? null;
    const allowed = !action.allowed;
    const apply = (to: boolean) => {
      const update = (current: ApprovalGroup) => withAllowed(current, action.action, to);
      if (id === null) {
        setIndex((current) =>
          current.status === 'ok' ? { status: 'ok', value: current.value.map(update) } : current,
        );
        return;
      }
      setDetails((current) => {
        const entry = current[id];
        return entry?.status === 'ok'
          ? { ...current, [id]: { status: 'ok', value: update(entry.value) } }
          : current;
      });
      setIndex((current) =>
        current.status === 'ok'
          ? {
              status: 'ok',
              value: current.value.map((entry) =>
                entry.connection?.id === id
                  ? { ...entry, allowed: Math.max(0, entry.allowed + (to ? 1 : -1)) }
                  : entry,
              ),
            }
          : current,
      );
    };
    const entry = { ...(id ? { connectionId: id } : {}), action: action.action };
    apply(allowed);
    setBusy(true);
    try {
      const client = await agentAccessApi.setApproval(
        grantId,
        family,
        allowed ? { allow: [entry] } : { disallow: [entry] },
      );
      if (alive.current) onChanged(client);
    } catch (error) {
      if (alive.current) {
        apply(!allowed);
        onNotice(approvalErrorMessage(error, copy, app), 'error');
        // A refused change usually means the list moved on: read it again.
        if (error instanceof CompanyRequestError && error.status === 409) {
          if (id === null) loadIndex();
          else loadDetail(id);
        }
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const groups = index.status === 'ok' ? index.value : null;
  const native = family !== 'data:connections';
  const allowedNow =
    groups === null ? approval.allowedCount : groups.reduce((sum, group) => sum + group.allowed, 0);
  const total = native ? (groups?.[0]?.total ?? approval.nativeTotal) : undefined;
  const count =
    allowedNow === 0
      ? copy.allowedNone
      : native && total !== undefined
        ? formatCopy(copy.allowedOf, { allowed: String(allowedNow), total: String(total) })
        : formatCount(copy.allowedActions, allowedNow, locale);

  const groupCount = (group: ApprovalGroup) => {
    // The server says why a group lists nothing: its connection is off or set to Read,
    // it has no write tools, or every write it has is one that always asks.
    if (group.connection?.access === 'off') return copy.groupOff;
    if (group.connection?.access === 'read') return copy.groupReadOnly;
    if (group.status === 'unavailable') return copy.groupUnavailable;
    if (group.total === 0)
      return group.locked > 0 ? copy.groupSendingAsks : copy.groupNothingToChange;
    return formatCopy(copy.allowedOf, {
      allowed: String(group.allowed),
      total: String(group.total),
    });
  };

  return (
    <Collapsible className="ws-allow" open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="ws-allow-head">
        <span className="ws-allow-title">{copy.allowedTitle}</span>
        <span className="ws-allow-count">{count}</span>
        <WorkspaceIcon name="down" size={14} className="ws-allow-chevron" />
      </CollapsibleTrigger>
      {open && (
        <CollapsibleContent className="ws-allow-body">
          {index.status === 'failed' ? (
            <p className="ws-allow-status" role="status">
              {copy.loadFailed}{' '}
              <button type="button" className="ws-allow-more" onClick={loadIndex}>
                {copy.retry}
              </button>
            </p>
          ) : !groups ? (
            <p className="ws-allow-status" role="status">
              {copy.loading}
            </p>
          ) : native ? (
            groups.map((group) => (
              <Rows
                key="native"
                group={group}
                name=""
                readOnly={false}
                busy={busy}
                onToggle={(action) => void change(group, action)}
              />
            ))
          ) : (
            groups.map((group) => {
              const connection = group.connection;
              if (!connection) return null;
              const readOnly = connection.access !== 'read_write';
              const expandable = !readOnly && group.status === 'ok' && group.total > 0;
              const expanded = expandable && openGroups.includes(connection.id);
              const detail = details[connection.id];
              const shown = detail?.status === 'ok' ? detail.value : group;
              const head = (
                <>
                  <ConnectionMark name={connection.name} src={connection.logo} />
                  <strong>{connection.name}</strong>
                  <span className="ws-allow-count">{groupCount(shown)}</span>
                  <WorkspaceIcon name="down" size={14} className="ws-allow-chevron" />
                </>
              );
              const kind = readOnly ? 'blocked' : expandable ? undefined : 'locked';
              if (!expandable)
                return (
                  <div className="ws-allow-group" key={connection.id} data-kind={kind}>
                    <button type="button" className="ws-allow-group-head" disabled>
                      {head}
                    </button>
                  </div>
                );
              return (
                <Collapsible
                  className="ws-allow-group"
                  key={connection.id}
                  open={expanded}
                  onOpenChange={() => toggleGroup(connection.id)}
                >
                  <CollapsibleTrigger className="ws-allow-group-head">{head}</CollapsibleTrigger>
                  <CollapsibleContent>
                    {detail?.status === 'ok' ? (
                      <Rows
                        group={detail.value}
                        name={connection.name}
                        readOnly={false}
                        limit={SHOWN}
                        busy={busy}
                        onToggle={(action) => void change(detail.value, action)}
                      />
                    ) : detail?.status === 'failed' ? (
                      <p className="ws-allow-status" role="status">
                        {copy.loadFailed}{' '}
                        <button
                          type="button"
                          className="ws-allow-more"
                          onClick={() => loadDetail(connection.id)}
                        >
                          {copy.retry}
                        </button>
                      </p>
                    ) : (
                      <p className="ws-allow-status" role="status">
                        {copy.loading}
                      </p>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              );
            })
          )}
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}
